import type * as Struct from 'struct';
import type { Tender } from '~/models/tenders';
import type { FormComponentProps } from 'antd/lib/form';
import React from 'react';
import { routeUrl, withPreload, withRouteX } from '~/router';
import { FormWrapper } from 'Components/form';
import { mergeSchema } from 'Components/form/utils';
import { Alert, Button, Form } from 'Components/nui';
import { useMountedState } from '~/hooks';
import { promisify } from 'es6-promisify';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import * as services from '~/services';
import { FormattedMessage } from 'react-intl';
import Access from 'Components/Access';

type TenderProp = Record<'tender', Tender>;

interface IFixedPrice {
  mode?: string;
}
const FixedPrice = withPreload({
  route: 'tender-edit-fixedprice',
  preload: services.tenders.schemas.fixedPrice(),
})<IFixedPrice>(({ data: { tender, schema, orders }, mode = 'edit' }) => {
  return (
    <div className="tender-edit-step details">
      <FixedPriceForm
        tender={tender}
        schema={schema}
        mode={mode}
        orders={orders}
      />
    </div>
  );
});

export default withRouteX({
  name: 'tender-edit-fixedprice',
  exact: true,
})(({ params: { mode } }) => {
  return <FixedPrice mode={mode} />;
});

type IUseFormWrapper = FormComponentProps &
  Partial<TenderProp> &
  Record<'schema', Struct.Schema>;
type FormProps = IUseFormWrapper & Record<'mode', string>;
function useFormWrapper({ form, schema, tender }: IUseFormWrapper) {
  const fields = [];
  Object.entries(schema)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([name, spec]) => {
      fields.push({
        render: () => (
          <>
            <hr />
            <h3>
              <strong>{spec.label_category}</strong> {spec.label}{' '}
              <strong>{spec.label_date}</strong>
            </h3>
          </>
        ),
      });

      fields.push({
        render: () => (
          <>
            {spec.disabled && (
              <Alert className="mb-20" type="warning">
                <FormattedMessage
                  id="orderform-field-startprice-disabled-warning"
                  description="Label for 'Start price cannot be updated because a bid has been placed' field on new order form"
                  defaultMessage="Start price cannot be updated because a bid has been placed"
                />
              </Alert>
            )}
          </>
        ),
      });

      fields.push({
        name,
        label: (
          <FormattedMessage
            id="orderform-field-startprice-label"
            description="Label for 'Start price' field on new order form"
            defaultMessage="Start price"
          />
        ),
        initialValue: spec.value,
        step: spec.step,
        props: { disabled: spec.disabled },
        suffix: (
          <small className="light-gray">
            <FormattedMessage
              id="orderform-field-fixedprice-helptext"
              description="Help text for 'Fixed price' field help text on new order form"
              defaultMessage="Applies to {num, plural, one {# offer} other {# offers}}"
              values={{
                num: spec.offer_count,
              }}
            />
          </small>
        ),
      });
    });

  return new FormWrapper(form, tender?.formdata, mergeSchema(schema, fields));
}

const BaseForm = ({ form, schema, tender, mode, orders }: FormProps) => {
  const [loading, setLoading] = useMountedState(false);
  const history = useHistory();
  const formWrapper = useFormWrapper({ form, schema, tender, orders });

  async function handleSubmit() {
    setLoading(true);

    try {
      await promisify(form.validateFieldsAndScroll)();
    } catch (err) {
      setLoading(false);
      return;
    }

    const fields = formWrapper.serialize();
    const formdata = new FormData();
    for (const [name, value] of Object.entries(fields)) {
      if (value !== undefined) {
        if (Array.isArray(value))
          for (const v of value) formdata.append(name, v);
        else formdata.append(name, value as string);
      }
    }
    if (tender?.id) formdata.append('id', tender.id);
    const result = await services.tenders.actions.editTenderFixedPrice(
      formdata,
      tender.id
    );

    if (result) {
      if (result.success) {
        toast.success(`Fixed price index saved`);
        const url = routeUrl('tender-edit-review', {
          tenderId: tender.id,
          mode,
        });
        history.push(url);
      } else {
        if (result.errors) for (const e of result.errors) toast.error(e);
        if (result.validate) formWrapper.setErrors(result.validate);
      }
    }

    setLoading(false);
  }

  return (
    <>
      <Access to={['publish', 'notify']} model={tender}>
        <Alert type="info" hasicon className="mb-20">
          {tender.status === 'planning' && (
            <p>
              <FormattedMessage
                id="tender-create-fixed-price-planning-text"
                description="Text for fixed price section for tenders create/edit pages in planning status"
                defaultMessage="Please enter a fixed price for the below indexes. You can do this before or after you publish the tender, however it must be completed before the tender is active."
              />
            </p>
          )}
          {tender.status === 'published' && (
            <p>
              <FormattedMessage
                id="tender-create-fixed-price-published-text"
                description="Text for fixed price section for tenders create/edit pages in published status"
                defaultMessage="Please notify the participants of this tender after adding fixed prices."
              />
            </p>
          )}
        </Alert>
      </Access>

      <Form
        className="nui-form tender-form"
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {formWrapper.render()}
        <div className="sticky-btm button-set">
          <Button
            htmlType="submit"
            type="primary"
            disabled={loading}
            loading={loading}
          >
            <FormattedMessage
              id="tender-create-order-fixedprice-button-continue"
              description="Button for Continue on tenders create/edit fixed price page"
              defaultMessage="Continue"
            />
          </Button>
        </div>
      </Form>
    </>
  );
};

const FixedPriceForm = Form.create<FormProps>()(BaseForm);
